import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './SolutionsGif.css';
import DroneImage from '../assets/Drone_image.png';
import AirplaneV1 from '../assets/AirplaneV1.png';
import AirplaneV2 from '../assets/AirplaneV2.png';
import carImage from '../assets/Car_new_September.png';

function SolutionsGif() {
  const solutionsRef = useRef(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedPopup, setSelectedPopup] = useState(null);

  useEffect(() => {
    const observerOptions = { threshold: 0.3 };

    const observerCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const currentElement = solutionsRef.current;

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, []);

  const handleImageClick = (step) => {
    setSelectedStep((prevStep) => (prevStep === step ? null : step));
  };

  const handlePopupClick = (popupNumber) => {
    setSelectedPopup((prevPopup) => (prevPopup === popupNumber ? null : popupNumber));
  };

  // Close popup when scrolling away
  useEffect(() => {
    const handleScroll = () => {
      setSelectedStep(null);
      setSelectedPopup(null);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="solutions-gif" ref={solutionsRef}>
      <div className="solutions-container">
        {/* New eTOLS Content - Drone Launch System */}
        <div className="etols-content">
          <div className="etols-subtitle">
            <h2 className="etols-title">eTOLS</h2>
            <span>Electromagnetic Take-Off Launch System</span>
          </div>
          
          {/* Car Image with Numbered Popups */}
          <div className="car-image-section">
            <div className="car-image-container">
              <img src={carImage} alt="eTOLS Launch System mounted on pickup truck" className="car-image" />
              
              {/* Popup 1 - Fixed-wing Drone */}
              <div className="popup-number popup-1" onClick={() => handlePopupClick(1)}>
                1
              </div>
              {selectedPopup === 1 && (
                <div className="popup-overlay etols-popup">
                  <div className="popup-content">
                    <button className="close-popup" onClick={() => setSelectedPopup(null)}>X</button>
                    <h3>Fixed-wing Drone is loaded and secured to eTOLS</h3>
                    <p>The drone is positioned and secured on the launch rail, ready for electromagnetic propulsion.</p>
                  </div>
                </div>
              )}

              {/* Popup 2 - eTOLS electromagnetic motor */}
              <div className="popup-number popup-2" onClick={() => handlePopupClick(2)}>
                2
              </div>
              {selectedPopup === 2 && (
                <div className="popup-overlay etols-popup">
                  <div className="popup-content">
                    <button className="close-popup" onClick={() => setSelectedPopup(null)}>X</button>
                    <h3>eTOLS electromagnetic motor accelerates the UAV to take-off speeds</h3>
                    <p>Using electromagnetic propulsion technology, the system accelerates the drone to the required take-off velocity without traditional fuel consumption.</p>
                  </div>
                </div>
              )}

              {/* Popup 3 - Drone ejection */}
              <div className="popup-number popup-3" onClick={() => handlePopupClick(3)}>
                3
              </div>
              {selectedPopup === 3 && (
                <div className="popup-overlay etols-popup">
                  <div className="popup-content">
                    <button className="close-popup" onClick={() => setSelectedPopup(null)}>X</button>
                    <h3>As take-off speeds are achieved, eTOLS ejects the Drone for lift-off</h3>
                    <p>Once the drone reaches the necessary speed, it's released from the launch system to achieve autonomous flight.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="etols-features">
            <div className="features-section">
              <h3 className="section-title">Key Features</h3>
              <ul className="features-list">
                <li><strong>Electromagnetic Launch:</strong> No fuel, no emissions, no runway</li>
                <li><strong>Payload Capacity:</strong> Launch UAVs up to 100 kg (First Gen)</li>
                <li><strong>Portable & Modular:</strong> Vehicle-mounted & deployable in under 5 min</li>
                <li><strong>High Launch Rate:</strong> Up to 1 launch/min for swarm operations</li>
              </ul>
            </div>

            <div className="applications-section">
              <h3 className="section-title">Applications</h3>
              <ul className="applications-list">
                <li><strong>ISR Missions:</strong> Rapid deployment for surveillance and reconnaissance</li>
                <li><strong>Interceptor Ops:</strong> Support rapid-response and defensive drone launches</li>
                <li><strong>Mobile & Naval Platforms:</strong> Deploy from vehicles, convoys, or ships — no runway needed</li>
                <li><strong>Critical Logistics:</strong> Launch UAVs for military, humanitarian, and commercial supply in challenging environments</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Original eTOLS V1, V2, V3 sections - Hidden but not deleted */}
        {false && (
          <>
            {/* eTOLS V1 - Drones */}
            <div className="solution-step">
              <h3 className="solution-type">eTOLS V1</h3>
              <div className="step-image" onClick={() => handleImageClick(1)}>
                <img src={DroneImage} alt="Drones eTOLS V1" className="clickable-image" />
              </div>
              <p className="solution-name">Drones</p>
              {selectedStep === 1 && (
                <div className="popup-overlay">
                  <div className="popup-content">
                    <button className="close-popup" onClick={() => setSelectedStep(null)}>X</button>
                    <p>
                      eTOLS V1 is designed for drone operations, optimizing launch efficiency and energy consumption.
                    </p>
                    <Link to="/product#v1" className="learn-more">
                      LEARN MORE
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* eTOLS V2 - Commercial Aviation */}
            <div className="solution-step">
              <h3 className="solution-type">eTOLS V2</h3>
              <div className="step-image" onClick={() => handleImageClick(2)}>
                <img src={AirplaneV1} alt="Commercial Aviation eTOLS V2" className="clickable-image" />
              </div>
              <p className="solution-name">Commercial Aviation</p>
              {selectedStep === 2 && (
                <div className="popup-overlay">
                  <div className="popup-content">
                    <button className="close-popup" onClick={() => setSelectedStep(null)}>X</button>
                    <p>
                      eTOLS V2 is designed for commercial aviation, reducing emissions and improving take-off efficiency.
                    </p>
                    <Link to="/product#v2" className="learn-more">
                      LEARN MORE
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* eTOLS V3 - Electric Aircrafts */}
            <div className="solution-step">
              <h3 className="solution-type">eTOLS V3</h3>
              <div className="step-image" onClick={() => handleImageClick(3)}>
                <img src={AirplaneV2} alt="Electric Aircrafts eTOLS V3" className="clickable-image" />
              </div>
              <p className="solution-name">Electric Aircraft</p>
              {selectedStep === 3 && (
                <div className="popup-overlay">
                  <div className="popup-content">
                    <button className="close-popup" onClick={() => setSelectedStep(null)}>X</button>
                    <p>
                      eTOLS V3 represents the future of electric aircraft with zero-emission take-off technology.
                    </p>
                    <Link to="/product#v3" className="learn-more">
                      LEARN MORE
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default SolutionsGif;
